import { ReactNode, useState, useMemo, CSSProperties } from 'react';
import { Table as Table, TableProps, ProgressBar } from '@ets/design-system';

import { useDispatch, useEffectOnUpdate, usePrevious, useSelector } from '_common/hooks';
import { setSelected, listObjects, lazyLoad } from '_common/components/Table/TableSlice';

import Footer from './Footer/Footer';

import styles from './Table.module.scss';

import type { SortingState, TableColumn, TableRow } from './Table';

export type BaseTableProps<T extends TableRow = TableRow> = TableProps<T>;

export type ThunkTableProps<T extends TableRow = TableRow> = {
  // Define if table rows can be selected
  selectable?: boolean;
  loadingLabel: string;
  renderEmptyState?: () => ReactNode;
  renderFooter?: () => ReactNode;
  selection?: Record<string, boolean>;
  maxHeight?: CSSProperties['maxHeight'];
  id?: string;
} & Omit<BaseTableProps<T>, 'options'> & {
    options: Omit<BaseTableProps<T>['options'], 'columns'> & { columns: TableColumn<T>[] };
  } & (
    | {
        // Table identity in order to differenciate content from the other tables
        identity: Table.Identity;
        lazyLoading?: boolean;
        fetchObjects: (params: Request.AllListParams) => Promise<Request.FetchResponse>;
      }
    | {
        identity?: undefined;
        lazyLoading?: undefined;
        fetchObjects?: undefined;
      }
  );

const ThunkTable = <T extends TableRow = TableRow>({
  identity,
  selectable = true,
  selectRowOnClick = true,
  fetchObjects,
  renderEmptyState,
  renderFooter = () => <Footer />,
  lazyLoading,
  loadingLabel,
  getRowId,
  selection,
  maxHeight,
  id,
  testId,
  ...props
}: ThunkTableProps<T>) => {
  const { options } = props;
  const { data, columns } = options;

  const getPersistedRequestState = () => {
    if (identity) {
      const localStorageItem = localStorage.getItem(identity);
      if (!localStorageItem) {
        return {};
      }

      return JSON.parse(localStorageItem); // Holds order and filters
    }
  };

  const initialSorting = useMemo<SortingState>(() => {
    const localStorageData = getPersistedRequestState();
    if (localStorageData?.order_field) {
      return [{ id: localStorageData.order_field, desc: localStorageData.order_type === 'desc' }];
    }
    return [];
  }, [identity]);

  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const previousSorting = usePrevious(sorting);

  const dispatch = useDispatch();
  const contentLoading = useSelector((state) => state.table.contentLoading);
  const requestState = useSelector((state) => state.table.request); // Holds offset and size
  const identityState = useSelector((state) =>
    identity ? state.table.identity[identity] : undefined,
  );
  const selected = useSelector((state) => state.table.selected);

  //#region Handle selection
  const handleSelectionChange: TableProps<T>['onSelectionChange'] = (updater) => {
    const updatedState = typeof updater === 'function' ? updater(selected) : updater;
    dispatch(setSelected(updatedState));
  };
  //#endregion

  //#region Handle sorting
  useEffectOnUpdate(() => {
    /**
     * Only sort if the sorting has changed
     * Need this validation because an unecessary request would be made when "sorting" state is set
     */
    if (previousSorting && sorting) {
      if (
        previousSorting.length !== sorting?.length ||
        previousSorting[0]?.id !== sorting[0]?.id ||
        previousSorting[0]?.desc !== sorting[0]?.desc
      ) {
        const newOrder: Request.OrderParams = {
          order_field: sorting.length ? sorting[0].id : '_id',
          order_type: sorting.length ? (sorting[0].desc ? 'desc' : 'asc') : 'asc',
        };

        const request = { ...requestState, ...getPersistedRequestState(), ...newOrder, offset: 0 };
        if (identity) {
          dispatch(
            listObjects({
              identity,
              fetch: fetchObjects,
              request,
              cause: 'ORDER',
            }),
          );
        }
      }
    }
  }, [sorting]);
  //#endregion

  const tableMinWidth = useMemo(
    /**
     * The table has table-layout:fixed and the columns without a width defined (columns with a flex width)
     * can have a width of 0 when the table shrinks
     * To avoid this, we need to define a min-width for the table so it has enough space to render all the columns
     * The min-width will be the sum of all columns width or min-width, including prefix (32px) and suffix columns (32px)
     * This is assuming that the prefix and suffix columns have a width of 32px
     */
    () => columns.reduce((acc, column) => acc + (column.size ?? column.minWidth ?? 0), 0) + 32 + 32,
    [columns],
  );

  return (
    <div className={styles.root} id={id}>
      {contentLoading && (
        <div className={styles.loading}>
          <ProgressBar size="m" variant="primary-dark" testId={`table-${identity}`}>
            {loadingLabel}
          </ProgressBar>
        </div>
      )}

      {(!data || data.length < 1) && !contentLoading && renderEmptyState?.()}

      {!contentLoading && data && data.length > 0 && (
        <div className={styles.content}>
          <div
            style={{
              overflow: 'auto',
              height: '100%',
              maxHeight,
            }}
          >
            <Table
              {...props}
              sortingState={sorting}
              onSortingStateChange={setSorting}
              options={{
                manualSorting: true,
                manualPagination: true,
                ...options,
              }}
              getRowId={getRowId ? getRowId : (row) => row.id}
              selectable={selectable}
              selectRowOnClick={selectRowOnClick}
              onSelectionChange={selectable ? handleSelectionChange : undefined}
              rowSelectionState={selection ? selection : selectable ? selected : undefined}
              virtualizeRows
              style={{ minWidth: tableMinWidth ?? 'auto' }}
              height={'100%'}
              testId={testId}
              infiniteScrollData={
                lazyLoading && import.meta.env.MODE !== 'test'
                  ? {
                      fetchNextPage: () => {
                        const request = {
                          ...requestState,
                          ...getPersistedRequestState(),
                        };

                        dispatch(
                          lazyLoad({
                            identity,
                            fetch: fetchObjects,
                            request,
                          }),
                        );
                      },
                      isFetching: !!identityState?.isNextPageLoading,
                      totalDBRowCount: identityState?.total ?? 0,
                      totalFetched: data.length,
                    }
                  : undefined
              }
            />
          </div>
        </div>
      )}

      {!contentLoading && renderFooter()}
    </div>
  );
};

export default ThunkTable;
